import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Breadcrumbs from '../navigation/Breadcrumbs';
import { buildServiceBreadcrumbs } from '../navigation/breadcrumbUtils';
import { getBreadcrumbSchema } from '../navigation/breadcrumbSchema';
import SEO from '../seo/SEO';
import { SERVICE_ROUTES } from '../../constants/serviceRoutes';
import './HRServicesPage.css';

const HR_SERVICE_DETAILS = {
  BPOServices: {
    title: 'BPO Services',
    intro:
      'BlackRoth provides scalable business process outsourcing services that help organizations improve efficiency, reduce operational load, and deliver consistent business support.',
    overview:
      'Our BPO services cover repeatable business operations that require accuracy, speed, and clear process ownership. We help teams move non-core workflows into structured, measurable support models.',
    capabilities:
      'Each workflow is designed around defined SLAs, trained teams, quality checks, escalation paths, and reporting so daily operations stay dependable as business volume grows.',
    services: [
      'Business process assessment and workflow setup',
      'Voice, non-voice, and back-office process support',
      'Customer lifecycle and operations support',
      'Quality monitoring and process improvement',
      'SLA-based reporting and performance tracking',
      'Scalable team allocation for changing workloads',
    ],
    process:
      'We begin by mapping the process, documenting handoffs, defining quality rules, training the delivery team, running a controlled transition, and then operating with continuous monitoring.',
    value:
      'Businesses gain lower operating overhead, cleaner process execution, better turnaround times, and more internal bandwidth for strategic work.',
  },
  ContentModeration: {
    title: 'Content Moderation',
    intro:
      'BlackRoth supports safer digital platforms with structured content moderation for text, image, video, and user-generated content.',
    overview:
      'Our moderation workflows help brands protect users, enforce platform policies, and reduce exposure to harmful, inappropriate, or non-compliant content.',
    capabilities:
      'We combine policy-aligned review processes, trained moderators, escalation handling, and audit-ready reporting to maintain consistent moderation outcomes.',
    services: [
      'Text, image, video, and profile moderation',
      'User-generated content review',
      'Policy enforcement and escalation workflows',
      'Spam, abuse, and fraud signal review',
      'Community safety and brand protection support',
      'Moderation quality checks and reporting',
    ],
    process:
      'We define moderation guidelines, build category rules, train reviewers, set escalation levels, perform live review, and refine outcomes through regular quality audits.',
    value:
      'Platforms can maintain safer communities, protect brand trust, improve compliance, and respond faster to content risks.',
  },
  CustomerSupport: {
    title: 'Customer Support',
    intro:
      'BlackRoth delivers customer support operations that help businesses respond faster, resolve issues clearly, and improve customer satisfaction.',
    overview:
      'Our support services cover customer communication across chat, email, and voice channels with process discipline and service quality at the center.',
    capabilities:
      'We create support workflows for ticket routing, response templates, issue classification, escalation management, and performance reporting.',
    services: [
      'Chat, email, and voice support',
      'Ticket handling and issue resolution',
      'Customer onboarding and account assistance',
      'Complaint management and escalation support',
      'Knowledge base and response template support',
      'CSAT, SLA, and support performance reporting',
    ],
    process:
      'We study the customer journey, define support categories, train agents, set escalation rules, launch support operations, and improve continuously using customer feedback.',
    value:
      'Organizations improve customer response times, reduce unresolved tickets, increase satisfaction, and keep service quality consistent across channels.',
  },
  HRPayrollOperations: {
    title: 'HR & Payroll Operations',
    intro:
      'BlackRoth manages HR and payroll operations that keep employee data, attendance, payroll cycles, and compliance workflows organized.',
    overview:
      'This service supports day-to-day people operations by connecting employee records, payroll inputs, leave data, attendance, and statutory requirements into a dependable workflow.',
    capabilities:
      'We help manage employee lifecycle operations, payroll coordination, compliance documentation, reporting, and recurring HR administrative tasks.',
    services: [
      'Employee onboarding and offboarding support',
      'Attendance, leave, and shift data coordination',
      'Payroll input preparation and validation',
      'Employee records and document management',
      'Compliance support and HR reporting',
      'HR helpdesk and workforce operations support',
    ],
    process:
      'We gather HR process requirements, clean employee data, define payroll input checkpoints, coordinate approval workflows, and maintain recurring operational calendars.',
    value:
      'HR teams get fewer manual errors, cleaner payroll cycles, better employee record control, and more time for strategic workforce planning.',
  },
  RPO: {
    title: 'Recruitment Process Outsourcing',
    intro:
      'BlackRoth provides recruitment process outsourcing support for sourcing, screening, coordination, and hiring operations.',
    overview:
      'Our RPO services help companies manage hiring demand with structured recruitment workflows, candidate pipelines, and clear communication.',
    capabilities:
      'We support requisition intake, sourcing strategy, candidate screening, interview coordination, feedback tracking, and onboarding handoffs.',
    services: [
      'Job requirement intake and hiring workflow setup',
      'Candidate sourcing and database screening',
      'Resume shortlisting and initial candidate evaluation',
      'Interview scheduling and candidate coordination',
      'Offer coordination and onboarding handoff',
      'Recruitment reports and pipeline tracking',
    ],
    process:
      'We understand each role, define screening criteria, source candidates, qualify profiles, coordinate interviews, track feedback, and support closure.',
    value:
      'Businesses can reduce hiring delays, improve candidate quality, maintain pipeline visibility, and scale recruitment support when hiring volume increases.',
  },
  BackOfficeOperations: {
    title: 'Back Office Operations',
    intro:
      'BlackRoth supports back-office operations that keep administrative workflows accurate, organized, and easy to track.',
    overview:
      'Our back-office services handle behind-the-scenes operational tasks that are essential for smooth business execution but often consume internal team time.',
    capabilities:
      'We provide process documentation, data handling, document coordination, operational tracking, and structured administrative support.',
    services: [
      'Administrative workflow support',
      'Document processing and record management',
      'Operational coordination and follow-ups',
      'Report preparation and data consolidation',
      'Vendor, customer, or employee record support',
      'Process tracking and quality review',
    ],
    process:
      'We map the back-office workflow, define task ownership, prepare templates, set review checkpoints, execute daily operations, and report progress.',
    value:
      'Teams gain cleaner documentation, faster task completion, reduced administrative pressure, and better operational visibility.',
  },
  DataEntryServices: {
    title: 'Data Entry Services',
    intro:
      'BlackRoth provides accurate and secure data entry services for records, documents, forms, and large-scale information management.',
    overview:
      'Our data entry workflows help organizations digitize, organize, validate, and maintain business information with consistency and confidentiality.',
    capabilities:
      'We handle structured entry, document indexing, data cleaning, validation checks, and secure record processing for high-volume requirements.',
    services: [
      'Manual and bulk data entry',
      'Document digitization and indexing',
      'Form processing and database updates',
      'Data validation, cleanup, and deduplication',
      'CRM, HRMS, and spreadsheet data support',
      'Confidential records handling and reporting',
    ],
    process:
      'We define data fields, prepare templates, capture data, validate entries, review exceptions, and deliver organized outputs in the required format.',
    value:
      'Businesses receive cleaner records, reduced manual backlog, improved data reliability, and faster access to usable information.',
  },
  TechnicalSupport: {
    title: 'Technical Support',
    intro:
      'BlackRoth delivers technical support services for troubleshooting, user assistance, issue tracking, and IT operations support.',
    overview:
      'Our technical support helps users and teams resolve product, platform, and system issues through clear triage and structured escalation.',
    capabilities:
      'We support helpdesk operations, ticket triage, knowledge base workflows, incident escalation, and technical communication across support channels.',
    services: [
      'L1 and L2 technical support assistance',
      'Ticket triage and issue classification',
      'Product, platform, and system troubleshooting',
      'User access and account support',
      'Incident escalation and resolution tracking',
      'Knowledge base updates and support reporting',
    ],
    process:
      'We document support categories, create resolution scripts, train support teams, manage tickets, escalate complex issues, and report recurring patterns.',
    value:
      'Organizations improve support consistency, reduce resolution time, identify recurring technical issues, and provide users with clearer assistance.',
  },
};

const AnimatedHeading = ({ as: Tag, className, children }) => {
  const text = typeof children === 'string' ? children : '';

  return (
    <Tag className={className} aria-label={text}>
      <span className="hr-heading-text" aria-hidden="true">
        {text.split('').map((char, index) => (
          <span
            key={`${text}-${index}`}
            className={`hr-heading-letter${
              char === ' ' ? ' hr-heading-letter-space' : ''
            }`}
            style={{ '--letter-index': index }}
          >
            {char === ' ' ? '\u00A0' : char}
          </span>
        ))}
      </span>
    </Tag>
  );
};

const HRServiceDetailPage = ({ serviceKey }) => {
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();
  const servicesIndexView = location.state?.servicesIndexView;
  const service = HR_SERVICE_DETAILS[serviceKey] || HR_SERVICE_DETAILS.BPOServices;
  const backPath =
    servicesIndexView === 'hr' ||
    location.pathname.startsWith('/services/hr/')
      ? SERVICE_ROUTES.hrIndex
      : servicesIndexView
        ? SERVICE_ROUTES.index
        : '/';

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (prefersReducedMotion) {
      setIsVisible(true);
      return;
    }

    const timer = window.setTimeout(() => {
      setIsVisible(true);
    }, 80);

    return () => window.clearTimeout(timer);
  }, []);

  const breadcrumbItems = buildServiceBreadcrumbs('hr', service.title);
  const breadcrumbSchema = getBreadcrumbSchema(breadcrumbItems);

  return (
    <div className="standard-page-wrapper">
      <SEO schema={breadcrumbSchema} />
      <div className="standard-page-container">
        <Breadcrumbs items={breadcrumbItems} />

        <div className="standard-back-link">
          <Link
            to={backPath}
            state={servicesIndexView ? { servicesIndexView } : undefined}
          >
            {servicesIndexView ? '\u2190 Back' : '\u2190 Back to Home'}
          </Link>
        </div>

        <div className="standard-page-hero">
          <AnimatedHeading
            as="h1"
            className={`hr-main-heading hr-heading-hero${
              isVisible ? ' is-visible' : ''
            }`}
          >
            {service.title}
          </AnimatedHeading>
          <p>{service.intro}</p>
        </div>

        <section className="standard-page-section">
          <h2>Overview</h2>
          <p>{service.overview}</p>
        </section>

        <section className="standard-page-section">
          <h2>Core Capabilities</h2>
          <p>{service.capabilities}</p>
        </section>

        <section className="standard-page-section">
          <h2>Services We Offer</h2>
          <ul className="standard-service-list">
            {service.services.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="standard-page-section">
          <h2>Process</h2>
          <p>{service.process}</p>
        </section>

        <section className="standard-page-section">
          <h2>Business Value</h2>
          <p>{service.value}</p>
        </section>
      </div>
    </div>
  );
};

export default HRServiceDetailPage;
